import React, { useEffect, useRef, memo, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import {
  TRADINGVIEW_CSS_CLASSES,
  TRADINGVIEW_SCRIPTS,
  TRADINGVIEW_ERROR_MESSAGES,
  WIDGET_LOAD_TIMEOUT,
} from "@/constants/technicalAnalysis/tradingView";

interface MacroChartProps {
  symbol: string;
  interval?: string;
  height?: number;
  className?: string;
  theme?: "light" | "dark";
  title?: string;
  compareSymbols?: Array<{
    symbol: string;
    position: string;
  }>;
}

/**
 * MacroChart
 *
 * Specialized TradingView chart for Macro Analysis.
 * Features:
 * - Strict interval handling to prevent "1D title but 30m view" mismatch
 * - No Moving Averages by default
 * - Supports compare symbols
 */
export const MacroChart: React.FC<MacroChartProps> = ({
  symbol,
  interval = "D", // Default to Daily
  height = 400,
  className = "",
  theme = "dark",
  title,
  compareSymbols,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    if (!chartWrapperRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await chartWrapperRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear existing content
    container.innerHTML = "";
    scriptLoadedRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const widgetContainer = document.createElement("div");
    widgetContainer.className = TRADINGVIEW_CSS_CLASSES.WIDGET;
    widgetContainer.style.height = "100%";
    widgetContainer.style.width = "100%";
    container.appendChild(widgetContainer);

    const script = document.createElement("script");
    script.src = TRADINGVIEW_SCRIPTS.ADVANCED_CHART;
    script.type = "text/javascript";
    script.async = true;

    // Strict interval mapping
    const getSafeInterval = (int: string) => {
      if (int === "1D") return "D";
      if (int === "1W") return "W";
      if (int === "1M") return "M";
      return int;
    };

    const safeInterval = getSafeInterval(interval);

    const widgetConfig = {
      autosize: true,
      symbol: symbol,
      interval: safeInterval,
      timezone: "Etc/UTC",
      theme: theme,
      style: "1",
      locale: "en",
      backgroundColor: theme === "dark" ? "#1f2937" : "#ffffff",
      gridColor: theme === "dark" ? "rgba(242, 242, 242, 0.06)" : "rgba(0, 0, 0, 0.06)",
      hide_top_toolbar: true,
      hide_side_toolbar: true,
      allow_symbol_change: false,
      save_image: true,
      calendar: false,
      hide_volume: false,
      support_host: "https://www.tradingview.com",
      ...(compareSymbols && { compareSymbols }),
      studies: [],
    };

    script.innerHTML = JSON.stringify(widgetConfig);

    script.onerror = () => {
      console.error(TRADINGVIEW_ERROR_MESSAGES.SCRIPT_LOAD_FAILED);
      if (container) {
        container.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-800 rounded text-red-400">
            <div class="text-center">
              <p class="text-sm">${TRADINGVIEW_ERROR_MESSAGES.SCRIPT_LOAD_FAILED}</p>
            </div>
          </div>
        `;
      }
    };

    script.onload = () => {
      scriptLoadedRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    timeoutRef.current = setTimeout(() => {
      if (!scriptLoadedRef.current && containerRef.current) {
        console.warn("TradingView widget loading timeout");
        containerRef.current.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-800 rounded text-yellow-400">
            <div class="text-center">
              <p class="text-sm">Chart loading...</p>
            </div>
          </div>
        `;
      }
    }, WIDGET_LOAD_TIMEOUT);

    widgetContainer.appendChild(script);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [symbol, interval, theme, compareSymbols]);

  return (
    <div
      ref={chartWrapperRef}
      className={`bg-gray-800 rounded-xl border border-gray-700 p-4 shadow-lg transition-all duration-300 ${
        isFullscreen ? "fixed inset-0 z-50 rounded-none border-none p-6" : ""
      } ${className}`}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h3 className="font-semibold mr-2 text-slate-200">
            {title || symbol.split(":")[1]?.replace("USDT", "/USD") || symbol}
          </h3>
          <span className="text-xs bg-indigo-900/50 text-indigo-300 px-2 py-0.5 rounded">
            {interval} Chart
          </span>
        </div>
        <button
          className="p-1.5 bg-gray-700 rounded text-gray-300 hover:bg-gray-600 transition-colors"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen (ESC)" : "Enter Fullscreen"}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      <div
        ref={containerRef}
        className={TRADINGVIEW_CSS_CLASSES.CONTAINER}
        style={{
          height: isFullscreen ? "calc(100vh - 120px)" : `${height}px`,
          width: "100%",
        }}
      >
        <div className="flex items-center justify-center h-full bg-gray-700/50 rounded animate-pulse">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-400">Loading chart...</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(MacroChart);
